import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { Button } from 'primeng/button';
import { Checkbox } from 'primeng/checkbox';
import { DatePicker } from 'primeng/datepicker';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { Table, TableModule } from 'primeng/table';
import { Paginator } from 'primeng/paginator';
import { Card } from 'primeng/card';
import { AnnouncementsApi } from '../../../../../shared/services/api/announcements.api';
import { AnnouncementRecipient } from '../../../../../shared/models/announcement-recipient';
import { AnnouncementRecipientSearchCriteria } from '../../../../../shared/models/announcement-recipient-search-criteria';
import { Observable, of } from 'rxjs';
import { ControlErrorDirective } from '../../../../../shared/directive/control-error.directive';
import { PreviewDataWrapperComponent } from '../../../../../shared/components/preview-data-wrapper/preview-data-wrapper.component';
import { PreviewDataType } from '../../../../../shared/enums/preview-data-type.enum';
import { TableBaseAbstractComponent } from '../../../../../shared/components/table-base-abstract.component';
import { TableRequestData } from '../../../../../shared/models/table/table-request-param';
import { TableResponse } from '../../../../../shared/models/table-response';
import { SortDirection } from '../../../../../shared/enums/sort-direction.enum';
import { InfoMessageComponent } from '../../../../../shared/components/info-message/info-message.component';
import { AnnouncementRecipientDialogFormBuilder } from './form-group/announcement-recipient-dialog-form.builder';
import { RequiredLabelDirective } from '../../../../../shared/directive/required-label.directive';

@Component({
  selector: 'app-announcement-recipient-dialog',
  imports: [
    Button,
    Card,
    Checkbox,
    ControlErrorDirective,
    DatePicker,
    DatePipe,
    FormsModule,
    InputText,
    Paginator,
    ReactiveFormsModule,
    Select,
    TableModule,
    PreviewDataWrapperComponent,
    InfoMessageComponent,
    RequiredLabelDirective,
  ],
  templateUrl: './announcement-recipient-dialog.html',
  styleUrl: './announcement-recipient-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AnnouncementsApi],
})
export class AnnouncementRecipientDialog extends TableBaseAbstractComponent<AnnouncementRecipient> {
  @ViewChild('table') override tableRef!: Table;
  protected readonly dialogRef = inject(DynamicDialogRef);
  protected readonly reasonOptions = [
    {
      label: 'Wysłanie komunikatu',
      value: 'Wysłanie komunikatu',
    },
    {
      label: 'Weryfikacja z powodu zgłoszenia HD',
      value: 'Weryfikacja z powodu zgłoszenia HD',
    },
    {
      label: 'Weryfikacja na wniosek z innego urzędu',
      value: 'Weryfikacja na wniosek z innego urzędu',
    },
    {
      label: 'Na wniosek użytkownika',
      value: 'Na wniosek użytkownika',
    },
  ];
  protected readonly PreviewDataType = PreviewDataType;
  protected readonly today = new Date();
  protected searchForm =
    AnnouncementRecipientDialogFormBuilder.createFormGroup();
  protected selectedRecipients = signal<AnnouncementRecipient[]>([]);
  protected hasSearched = signal(false);
  protected searchCriteria = signal<AnnouncementRecipientSearchCriteria | null>(
    null,
  );
  readonly #config = inject(DynamicDialogConfig);
  readonly #announcementsApi = inject(AnnouncementsApi);

  constructor() {
    super();
    this.selectedRecipients.set(this.#config.data?.selectedRecipients ?? []);
    this.tableRequest.update((value) => ({
      ...value,
      pageSize: 5,
    }));
  }

  protected override apiCall$ = (
    params: TableRequestData,
  ): Observable<TableResponse<AnnouncementRecipient>> => {
    const normalizedParams = {
      ...params,
      sortBy: params.sortBy ?? 'lastName',
      order: params.order ?? SortDirection.ASC,
      pesel: this.searchCriteria()?.pesel ?? null,
      firstName: this.searchCriteria()?.firstName ?? null,
      lastName: this.searchCriteria()?.lastName ?? null,
      documentNumber: this.searchCriteria()?.documentNumber ?? null,
      birthDate: this.searchCriteria()?.birthDate ?? null,
      email: this.searchCriteria()?.email ?? null,
      city: this.searchCriteria()?.city ?? null,
      reason: this.searchCriteria()?.reason ?? null,
    };

    if (!this.hasSearched()) {
      return of({
        content: [],
        page: {
          size: normalizedParams.pageSize ?? 10,
          number: normalizedParams.pageIndex ?? 0,
          totalElements: 0,
          totalPages: 0,
        },
      });
    }

    return this.#announcementsApi.searchAnnouncementRecipients$(
      normalizedParams,
    );
  };

  protected onSearch(): void {
    if (this.searchForm.invalid) {
      this.searchForm.markAllAsTouched();
      return;
    }

    this.hasSearched.set(true);
    this.searchCriteria.set(this.#buildSearchCriteria());
    this.tableRequest.update((value) => ({
      ...value,
      pageIndex: 0,
    }));
    this.reloadData();
  }

  protected onClearFilters(): void {
    this.searchForm.reset();
    this.rows.set([]);
    this.tablePage.set(undefined);
    this.hasSearched.set(false);
  }

  protected isRecipientSelected(recipient: AnnouncementRecipient): boolean {
    return this.selectedRecipients().some(
      ({ email }) => email === recipient.email,
    );
  }

  protected toggleRecipient(
    recipient: AnnouncementRecipient,
    checked: boolean,
  ): void {
    const selectedRecipients = this.selectedRecipients();

    if (checked) {
      this.selectedRecipients.set([...selectedRecipients, recipient]);
      return;
    }

    this.selectedRecipients.set(
      selectedRecipients.filter(({ email }) => email !== recipient.email),
    );
  }

  protected onCancel(): void {
    this.dialogRef.close();
  }

  protected onChoose(): void {
    if (this.selectedRecipients().length === 0) {
      return;
    }

    this.dialogRef.close(this.selectedRecipients());
  }

  #buildSearchCriteria(): AnnouncementRecipientSearchCriteria {
    const rawValue = this.searchForm.getRawValue();

    return {
      pesel: rawValue.pesel?.trim() || null,
      firstName: rawValue.firstName?.trim() || null,
      lastName: rawValue.lastName?.trim() || null,
      documentNumber: rawValue.documentNumber?.trim() || null,
      birthDate: rawValue.birthDate,
      email: rawValue.email?.trim() || null,
      city: rawValue.city?.trim() || null,
      reason: rawValue.reason,
    };
  }
}
